<template>
    <div v-clickaway="close" :class="inputClass">
        <input
            ref="suggest"
            :value="prefill || value"
            :placeholder="placeholder"
            :autofocus="hasAutofocus"
            :required="isRequired"
            :disabled="isDisabled"
            @focus="focus($event)"
            @blur="blur($event)"
            @keydown.up.prevent="handleUpArrow($event)"
            @keydown.down.prevent="handleDownArrow($event)"
            @keydown.enter.prevent="handleEnter($event)"
            @input="input($event)"
        />

        <div ref="options" class="form-input-suggest__suggestions" @mouseleave="hoverSuggestion(-1)">
            <button
                v-for="(suggestion, index) in suggestions"
                ref="suggestion"
                :key="index"
                :class="classSuggestion(index)"
                type="button"
                tabindex="-1"
                @mouseover="hoverSuggestion(index)"
                @click="select(suggestion)"
            >
                <span>{{ suggestion.label }}</span>
            </button>
        </div>
        <p v-if="isFocused && !suggestions.length && showStartTyping" class="form-input-suggest__start-typing">
            <button v-if="!isLoading">
                <span>{{ getSuggestionLabel }}</span>
            </button>
            <common-loader v-else :isProcessing="isLoading"></common-loader>
        </p>
    </div>
</template>

<script>
    import FormInputMixin from 'mixins/FormInputMixin'
    import IndicateScrollMixin from 'mixins/IndicateScrollMixin'

    export default {
        mixins: [FormInputMixin, IndicateScrollMixin],

        props: {
            value: { type: [String, Number], default: undefined },
            prefill: { type: [String, Number], default: undefined },
            placeholder: { type: String, default: undefined },
            suggestions: { type: Array, required: true },
            isSuggested: { type: Boolean, default: false },
            isLoading: { type: Boolean, default: false },
            showStartTyping: { type: Boolean, default: false },
        },

        data() {
            return {
                isFocused: false,
                isOpen: false,
                highlightedSuggestion: null,
            }
        },

        computed: {
            hasSuggestions() {
                return this.suggestions.length
            },

            getSuggestionLabel() {
                return this.value?.length > 2
                    ? this.$t('common.microcopy.no_results_found')
                    : this.$t('common.microcopy.start_typing')
            },

            inputClass() {
                return {
                    'form-input-suggest': true,
                    'form-input-suggest--is-invalid': this.isInvalid,
                    'form-input-suggest--is-disabled': this.isDisabled,
                    'form-input-suggest--is-open': this.isOpen && this.hasSuggestions,
                    'form-input-suggest--is-suggested': this.isSuggested,
                    'form-input-suggest--is-prefilled': Boolean(this.prefill),
                }
            },
        },

        methods: {
            classSuggestion(suggestionIndex) {
                return {
                    'form-input-suggest__suggestion': true,
                    'form-input-suggest__suggestion--is-highlighted': suggestionIndex === this.highlightedSuggestion,
                }
            },

            hoverSuggestion(index) {
                this.highlightedSuggestion = index
                this.emitEvent({ name: 'hoverSuggestion', payload: this.suggestions[index] })
            },

            setInputText(value) {
                this.$refs.suggest.value = value
            },

            scrollToHighlightedSuggestion() {
                if (!this.$refs.suggestion) {
                    return
                }

                this.$refs.suggestion[Math.max(this.highlightedSuggestion, 0)].scrollIntoView({ block: 'nearest' })
            },

            switchHighlightedSuggestion(direction = 1) {
                const position = this.highlightedSuggestion + direction

                if (position < 0) {
                    this.highlightedSuggestion = this.suggestions.length - 1
                } else {
                    this.highlightedSuggestion = position % this.suggestions.length
                }

                this.scrollToHighlightedSuggestion()
            },

            open() {
                this.isOpen = true
            },

            close() {
                this.isOpen = false
                this.isFocused = false
            },

            focus() {
                if (this.isSuggested) {
                    return
                }
                this.isFocused = true
            },

            blur(event) {
                if (event.relatedTarget && !this.isDescendant(event.relatedTarget, this.$el)) {
                    this.close()
                }
            },

            input(value) {
                this.isFocused = true
                this.change(value)
                this.emitEvent({ name: 'select', payload: '' })
                this.open()
            },

            select({ label, value }) {
                this.change(label)
                this.emitEvent({ name: 'select', payload: value })
                this.close()
                this.setInputText(label)
            },

            handleUpArrow() {
                if (this.hasSuggestions) {
                    this.switchHighlightedSuggestion(-1)
                }
            },

            handleDownArrow() {
                if (this.hasSuggestions) {
                    this.switchHighlightedSuggestion(1)
                }
            },

            handleEnter() {
                if (this.isOpen && this.hasSuggestions) {
                    this.select(this.suggestions[this.highlightedSuggestion])
                } else {
                    this.toggle()
                }
            },
        },

        watch: {
            value(value) {
                this.setInputText(value)
            },

            suggestions() {
                this.indicateScrollability()
            },
        },
    }
</script>
